import { assert, error, log } from '../app/logger.js';
import { services } from '../app/services.js';
import { Schema } from '../util/schema.js';
import { PipeableObject } from '../base/pipeable/pipeable_object.js';
import { TripEntities } from './trip_entities.js';

class Trip extends PipeableObject {
  get id() {
    return this.data.id;
  }

  get title() {
    return this.data.title;
  }

  get status() {
    return this.data.status;
  }

  get activities() {
    return this.data.activities;
  }

  get startDate() {
    return this.data.startDate;
  }

  get duration() {
    return this.data.duration;
  }

  get groupSize() {
    return this.data.groupSize;
  }

  get cover() {
    return this.data.cover;
  }

  get loc() {
    return this.data.loc;
  }

  get rights() {
    return this.data.rights;
  }

  get inviteHash() {
    return this.data.inviteHash;
  }

  get cocampers() {
    return this.data.cocampers || [];
  }

  get entities() {
    if (!this.#tripEntities) {
      this.#tripEntities = new TripEntities(this);
    }
    return this.#tripEntities;
  }

  // Return year of the trip.
  get year() {
    if (this.yearCached === undefined) {
      this.yearCached = this.computeYear();
    }
    return this.yearCached;
  }

  // Returns true if the trip is planned, otherwise false (past trip).
  get isPlanned() {
    if (this.isPlannedCached === undefined) {
      this.isPlannedCached = this.computeIsPlanned();
    }
    return this.isPlannedCached;
  }

  get dates() {
    return {
      startDate: this.startDate,
      duration: this.duration,
    };
  }

  get locNDates() {
    return {
      loc: this.loc,
      startDate: this.startDate,
      duration: this.duration,
    };
  }

  get options() {
    return {
      activities: this.activities,
      startDate: this.startDate,
      duration: this.duration,
      groupSize: this.groupSize,
      loc: this.loc,
      cocampers: this.cocampers,
    };
  }

  get stringIdentifier() {
    return `${this.id}`;
  }

  is({ id }) {
    return this.id == id;
  }

  stringify() {
    let cocampers = this.cocampers
      .map(
        s =>
          `${s.inviteeEmail ? `<${s.inviteeEmail}>` : ''}${
            s.inviteeUserId ? `:${s.inviteeUserId}` : ''
          }`
      )
      .join(', ');
    return `${this.stringIdentifier}:'${this.title}'${
      cocampers ? `, cocampers: ${cocampers}` : ''
    }`;
  }

  toString() {
    return this.stringify();
  }

  setTitle(title) {
    assert(title !== undefined, `[trip] trip.setTitle: no title`);
    log(`[trip] save ${this.stringIdentifier} trip title`);

    this.setProperty('title', title);
    return services.trip.setProperty({
      id: this.id,
      title,
    });
  }

  setOption(option, value) {
    assert(option, `[trip-planning] setOption: no option`);
    assert(
      value !== undefined,
      `[trip-planning] setOption: ${option} has no value`
    );
    log(
      `[trip] save ${this.stringIdentifier} list for '${option}' option changed to ${value}`
    );

    this.setProperty(option, value);

    let updateData = null;
    switch (option) {
      case 'groupSize':
        if (this.option != value) {
          updateData = { groupSize: value };
        }
        break;

      case 'loc':
        if (this.loc.id != value.id) {
          updateData = { locId: value.id };
        }
        break;

      case 'startDate':
        if (this.option != value) {
          updateData = { startDate: value };
        }
        break;

      default:
        if (this.option != value) {
          updateData = {};
          updateData[option] = value;
        }
    }

    if (!updateData) {
      return null;
    }

    // Update trip data on server.
    return services.trip.setProperty(
      Object.assign(
        {
          id: this.id,
        },
        updateData
      )
    );
  }

  // Sends/revokes invites.
  async shareOrRevoke({ invitees, revokedInviteHashes = [] }) {
    let cocampers = await services.trip.shareOrRevoke({
      id: this.id,
      invitees,
      revokedInviteHashes,
    });
    this.setProperty('cocampers', cocampers);
  }

  async reloadCocampers() {
    // TODO: should be switching to pipeable mechanism.
    this.data.cocampers = await services.trip.getCocampers(this.id);
  }

  // implementation
  computeIsPlanned() {
    try {
      let endDate = Schema.Date(this.startDate);
      endDate.setUTCDate(endDate.getUTCDate() + parseInt(this.duration) - 1);
      endDate.setUTCHours(23, 59, 59);

      return Date.now() <= endDate.getTime();
    } catch (e) {
      error(`[data] failed to compute trip.isPlanned`, e);
      return true;
    }
  }

  computeYear() {
    try {
      let startDate = Schema.Date(this.startDate);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        timeZone: 'UTC',
      }).format(startDate);
    } catch (e) {
      error(`[data] failed to compute trip.year`, e);
      return '';
    }
  }

  constructor(data) {
    super();
    this.set(data);
  }

  get data() {
    return super.data || {};
  }

  static dataDeps = {
    dates: ['startDate', 'duration'],
    locNDates: ['loc', 'startDate', 'duration'],
    isPlanned: ['startDate', 'duration'],
    options: [
      'activities',
      'startDate',
      'duration',
      'groupSize',
      'loc',
      'cocampers',
    ],
    year: ['startDate'],
  };

  #tripEntities;
}

export { Trip };
